import { Behavior } from '@dia-ui/abstract';
import CustomSelect from "custom-select.js/customselect.all.min.js";

@Behavior('select')
export class Select {
  element: any;

  type: string;
  selected: string;
  config: any;

  select: CustomSelect;
  selectMutation: boolean = false;
  searchString: string;
  oldValue: string;

  // This is called only once, given the `element` that the behavior is attached to.
  constructor(element: HTMLElement) {
    this.element = element;

    this.type = this.element.getAttribute("data-select-type") || "dropdown";

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "data-select-selected") {
          setTimeout(() => {
            this.element.querySelectorAll('option').forEach((option) => option.removeAttribute("selected"));
            if (this.element.parentElement) {
              this.element.parentElement.querySelectorAll('.cs-option').forEach((option) => option.setAttribute("aria-selected", "false"));

              this.selected = this.element.getAttribute("data-select-selected") || false;
              if (this.selected) {
                let option = this.element.querySelector('option[value="' + this.selected + '"]');
                if (option) {
                  option.setAttribute("selected", "selected");
                  this.oldValue = this.select.getSelectedValue();
                  this.select.setSelectedValue(this.selected);
                  this.element.parentElement.querySelector('.cs-label').innerHTML = option.text;
                }
              }
            }
          }, 1000);
        }

        if (mutation.type === "childList") {
          setTimeout(() => {
            this.element.querySelectorAll('option').forEach((option) => option.removeAttribute("selected"));
            if (this.element.parentElement) {
              this.element.parentElement.querySelectorAll('.cs-option').forEach((option) => option.setAttribute("aria-selected", "false"));

              this.selected = this.element.getAttribute("data-select-selected") || false;
              let option = mutation.addedNodes[0] as HTMLOptionElement;

              if (this.selected && this.selected !== "" && option && option.value === this.selected) {
                option.setAttribute("selected", "selected");
                this.oldValue = this.select.getSelectedValue();
                this.select.setSelectedValue(this.selected);
                this.element.parentElement.querySelector('.cs-label').innerHTML = option.text;
              }
            }
          }, 500);
        }

        if (mutation.type === "childList" && this.searchString === "") {
          this.select.destroy();
          this.init();
        }
      });
    });

    observer.observe(this.element, { attributes: true, childList: true });
  }

  /**
   * init
   */
  init() {
    this.select = new CustomSelect(this.element, this.prepareConfig(this.type));
    this.oldValue = undefined;

    this.element.addEventListener("mousedown", (e) => {
      e.preventDefault();

      setTimeout(() => {
        let dropdown = this.element.parentElement.querySelector('.cs-dropdown');

          if (!dropdown.classList.contains("cs-open-down")) {
            this.element.nextElementSibling.click();
          }
      }, 250);
    });

    this.element.nextElementSibling.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      setTimeout(() => {
        let dropdown = this.element.parentElement.querySelector('.cs-dropdown'),
            target = this.element.nextElementSibling;

        if (dropdown.getAttribute("aria-expanded") === "true") {
          target.setAttribute("aria-expanded", "false");
          target.classList.remove("cs-open-down");
          dropdown.classList.remove("cs-open-down");

          this.select.events.afterClose();
          return;
        }

        this.select.events.afterOpen();
      }, 750);
    });
  }

  // This is called any time the associated `element` is appended into the
  // DOM, passed in the `element`
  connectedCallback(){
    this.init();
  }

  // This is called any time the associated `element` is removed from the DOM,
  // passed in the `element`.
  disconnectedCallback() {
    if (this.select) {
      this.select.destroy();
    }
  }

  // As with custom elements, define which attributes of the associated
  // element that the behavior should react to.
  /*static observedAttributes = [
    'data-draggable-events',
    'data-draggable-selector'
  ];*/

  // This is called any time any of the `observedAttributes` of the associated
  // element have been changed, just like with Custom Elements but with the
  // additional passed in `element`.
  // attributeChangedCallback(attributeName, oldValue, newValue, element) {}

  // There is one additional API, unlike with Custom Elements. If `static
  // awaitElementDefined` is `true`, then the behavior will not be
  // instantiated and connected until its host element is defined and upgraded
  // (that is, if the host element is possibly a custom element, having a
  // hyphen in its name). If the host element has no hyphen in its name, then
  // this does not apply, and the behavior will be created and connected
  // immediately without waiting. If a possibly-custom element is removed
  // before it is defined, then a behavior will not be created and connected
  // at all (waiting will have been canceled).
  static awaitElementDefined = true // Default is false.

  /**
   * prepareConfig
   * @param type
   * @private
   */
  private prepareConfig(type: string) {
    let config: any = {
      settings: {
        allowDeselectAll: false,
        openPosition: "relative",
        openLocation: this.element.parentElement
      },
      events: {}
    };

    switch(type) {
      // dropdown
      case "dropdown":
        config.settings.searchEnable = true;
        break;

      // font-search
      case "font-search":
      // dropdown-search
      case "dropdown-search":
        let search = this.element.parentElement.querySelector('.cs-search');
        if (search) {
          config.settings.alwaysOpen = search.value !== "";
        }
        config.settings.searchEnable = true;
        config.settings.searchPlaceholder = "";
        config.settings.searchNoResult = "Keine Ergebnisse";
        break;
    }

    config.events.beforeChange = (value, oldValue, instance) => {
      this.oldValue = oldValue;
      value = typeof value !== "string" ? value[0] : value;

      if (value !== "" && oldValue !== value && oldValue != null && oldValue !== undefined) {
        let elementName = this.element.getAttribute("name");
        let event = new CustomEvent("select:" + elementName + ":beforeChange", {
          detail: {
            oldValue: oldValue,
            value: value,
            instance: instance,
            element: this.element
          }
        });
        window.dispatchEvent(event);
      }
    };

    config.events.afterChange = (newValue) => {
      if (newValue === "") return;

      this.handleSearchRender();

      setTimeout(() => {
        let option = this.element.querySelector('option[value="'+newValue+'"]');
        if (option) {
          if (this.oldValue !== newValue && this.oldValue != null) {
            this.element.setAttribute("data-select-selected", option.value);

            let elementName = this.element.getAttribute("name");
            let event = new CustomEvent("select:" + elementName + ":afterChange", {
              detail: {
                oldValue: this.oldValue,
                value: option.value,
                element: this.element
              }
            });
            window.dispatchEvent(event);

            const e= new Event("change");
            this.element.dispatchEvent(e);
          }
        }
      }, 1000);
    };

    config.events.beforeOpen = () => {
      this.searchString = "";

      this.handleSearchRender();

      if (this.type === "font-search") {
        let options = this.element.parentElement.querySelectorAll('.cs-option');
        options.forEach((element) => {
          let fontString = element.innerHTML,
              fontItalic = "font-style: italic;",
              fontBold = "font-weight: bold;",
              fontHasItalic = fontString.indexOf(" Italic") !== -1 ? fontItalic : '',
              fontHasBold = fontString.indexOf(" Bold") !== -1 ? fontBold : '',
              fontFamily = fontString.replace(" Bold","").replace(" Bold",""),
              cssString = 'font-family:"' + fontFamily + '";'+fontHasItalic+fontHasBold;

          element.setAttribute("style", cssString);
        });
      }
    };

    config.events.afterOpen = () => {
      this.searchString = "";
      this.handleSearchRender();
    };

    config.events.afterClose = () => {
      this.searchString = "";
      this.handleSearchRender();
    };

    return config;
  }

  /**
   * addSearchHandler
   * @param search
   * @private
   */
  private addSearchHandler(search: any){
    let delay;

    search.addEventListener("keyup", (_e) => {
      if (delay) {
        clearTimeout(delay);
      }

      delay = setTimeout(() => {
        const event = new CustomEvent("keyup", {
          detail: {
            event: _e,
            value: search.value
          }
        });

        this.searchString = search.value;

        this.element.dispatchEvent(event);
      }, 250);

      _e.preventDefault();
    });
  }

  /**
   * handleSearchRender
   */
  handleSearchRender() {
    if (this.element && this.element.hasAttribute("data-select-type")) {
      this.type = this.element.getAttribute("data-select-type");
    }

    if (this.type.indexOf('-search') === -1) {
      if (this.element !== undefined && this.element.parentElement !== undefined) {
        let search = this.element.parentElement.querySelector('.cs-search');
        if (search) search.style.cssText = 'display: none';
      }
    }

    if (this.type.indexOf('-search') !== -1) {
      if (this.element !== undefined && this.element.parentElement !== undefined) {
        let search = this.element.parentElement.querySelector('.cs-search');
        if (search) {
          search.setAttribute("placeholder", "");
          search.style.cssText = '';

          this.addSearchHandler(search);
        }
      }
    }
  }
}
